import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SendIcon from "@mui/icons-material/Send";
import VideocamIcon from "@mui/icons-material/Videocam";
import { Box, Avatar, IconButton, Typography } from "@mui/material";
import React, { useState } from "react";

// First update the MediaItem interface to include type and file
interface MediaItem {
  id?: string;
  url: string;
  type?: "image" | "video";
  file?: File;
}

interface InstagramPreviewProps {
  content: string;
  media: MediaItem[];
}

export const InstagramPreview: React.FC<InstagramPreviewProps> = ({ content, media }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const showNavigation = media.length > 1;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % media.length);
  };

  const previousImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + media.length) % media.length);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              border: "2px solid #EC4899", // Mimicking ring-pink-500
            }}
          />
          <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: "bold" }}>
            youraccount
          </Typography>
        </Box>
        <IconButton>
          <MoreHorizIcon fontSize="small" sx={{ color: "text.secondary" }} />
        </IconButton>
      </Box>

      {/* Media Display */}
      <Box sx={{ position: "relative" }}>
        {media.length > 0 ? (
          <Box sx={{ position: "relative", aspectRatio: "1 / 1" }}>
            {media[currentImageIndex].type === "video" ? (
              <Box
                component="video"
                src={media[currentImageIndex].url}
                controls
                autoPlay={false}
                muted
                sx={{ width: "100%", height: "100%", objectFit: "cover", backgroundColor: "#000" }}
              />
            ) : (
              <Box
                component="img"
                src={media[currentImageIndex].url}
                alt=""
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
            {showNavigation && (
              <>
                {/* Left Arrow */}
                <IconButton
                  onClick={previousImage}
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "common.white",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                  }}
                  size="small"
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                {/* Right Arrow */}
                <IconButton
                  onClick={nextImage}
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "common.white",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                  }}
                  size="small"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
                {/* Dots Indicator */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    gap: 0.5,
                  }}
                >
                  {media.map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        transition: "background-color 0.3s",
                        bgcolor:
                          index === currentImageIndex ? "primary.main" : "rgba(255,255,255,0.7)",
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
            {/* Video Indicator (for Instagram) */}
            {media[currentImageIndex].type === "video" && (
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  color: "white",
                  borderRadius: "50%",
                  width: 24,
                  height: 24,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <VideocamIcon fontSize="small" />
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              aspectRatio: "1 / 1",
              backgroundColor: "grey.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "grey.500",
            }}
          >
            Add a photo or video
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton>
              <FavoriteBorderIcon fontSize="small" sx={{ color: "text.primary" }} />
            </IconButton>
            <IconButton>
              <ChatBubbleOutlineIcon fontSize="small" sx={{ color: "text.primary" }} />
            </IconButton>
            <IconButton>
              <SendIcon fontSize="small" sx={{ color: "text.primary" }} />
            </IconButton>
          </Box>
          {showNavigation && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {currentImageIndex + 1} / {media.length}
            </Typography>
          )}
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
          0 likes
        </Typography>
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: "bold" }}>
            youraccount
          </Box>{" "}
          {content || (
            <Box component="span" sx={{ color: "grey.500" }}>
              Your caption will appear here...
            </Box>
          )}
        </Typography>
      </Box>
    </Box>
  );
};
